import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Fade,
} from "@mui/material";
import {
  ChevronRight,
  CalendarMonth,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const BackgroundBox = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "60vh",
  background: "linear-gradient(135deg, #000000 0%, #0a0a0a 50%, #000000 100%)",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    height: "80%",
    background: "radial-gradient(circle, rgba(193, 227, 40, 0.1) 0%, transparent 70%)",
    pointerEvents: "none",
  },
}));

const FloatingParticle = styled(Box)(({ delay, duration, left, top }) => ({
  position: "absolute",
  width: 4,
  height: 4,
  borderRadius: "50%",
  background: "rgba(193, 227, 40, 0.3)",
  left: `${left}%`,
  top: `${top}%`,
  animation: `${pulse} ${duration}s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const GlowBox = styled(Box)(({ theme }) => ({
  position: "relative",
  background: "rgba(0, 0, 0, 0.8)",
  backdropFilter: "blur(20px)",
  borderRadius: 24,
  border: "1px solid rgba(193, 227, 40, 0.3)",
  padding: "60px 80px",
  textAlign: "center",
  overflow: "hidden",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "rgba(193, 227, 40, 0.5)",
    boxShadow: "0 12px 40px rgba(193, 227, 40, 0.2)",
  },
  [theme.breakpoints.down('md')]: {
    padding: "40px 40px",
  },
  [theme.breakpoints.down('sm')]: {
    padding: "30px 20px",
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
  borderRadius: 16,
  padding: "16px 40px",
  fontSize: "1.1rem",
  fontWeight: 600,
  textTransform: "none",
  color: "#000",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(193, 227, 40, 0.2)",
  boxShadow: "0 4px 16px rgba(193, 227, 40, 0.2)",
  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%)",
    opacity: 0,
    transition: "opacity 0.4s ease",
  },
  "&:hover": {
    background: "linear-gradient(135deg, #d0f030 0%, #c1e328 100%)",
    transform: "translateY(-3px) scale(1.02)",
    boxShadow: "0 12px 32px rgba(193, 227, 40, 0.4)",
    "&::after": {
      opacity: 1,
    },
  },
  "& .MuiButton-endIcon": {
    transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  },
  "&:hover .MuiButton-endIcon": {
    transform: "translateX(6px) scale(1.1)",
  },
  [theme.breakpoints.down('sm')]: {
    padding: "12px 30px",
    fontSize: "1rem",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: "linear-gradient(135deg, rgba(193, 227, 40, 0.15) 0%, rgba(193, 227, 40, 0.05) 100%)",
  border: "1px solid rgba(193, 227, 40, 0.3)",
  marginBottom: theme.spacing(3),
  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  boxShadow: "0 4px 20px rgba(193, 227, 40, 0.2)",
  "&:hover": {
    transform: "scale(1.1) rotate(5deg)",
    boxShadow: "0 8px 32px rgba(193, 227, 40, 0.3)",
    borderColor: "rgba(193, 227, 40, 0.5)",
  },
  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
  },
}));

const ScheduleDemo = () => {
  const navigate = useNavigate();

  const handleScheduleClick = () => {
    navigate("/contact");
  };

  return (
    <BackgroundBox
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <FloatingParticle
          key={i}
          delay={Math.random() * 3}
          duration={2 + Math.random() * 3}
          left={Math.random() * 100}
          top={Math.random() * 100}
        />
      ))}

      <Container maxWidth="md">
        <Fade in timeout={1000}>
          <GlowBox>
            <Stack spacing={3} alignItems="center">
              {/* Icon */}
              <IconWrapper>
                <CalendarMonth 
                  sx={{ 
                    fontSize: { xs: 40, sm: 50 }, 
                    color: "#c1e328",
                    filter: "drop-shadow(0 0 12px rgba(193, 227, 40, 0.5))",
                  }} 
                />
              </IconWrapper>

              {/* Title */}
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: -1,
                }}
              >
                Ready to{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(135deg, #c1e328 0%, #a0c020 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Transform
                </Box>{" "}
                Your Operations?
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(203, 213, 225, 1)",
                  lineHeight: 1.8,
                  fontSize: { xs: "0.9rem", sm: "1rem", md: "1.125rem" },
                  maxWidth: 600,
                }}
              >
                See how our autonomous solutions can revolutionize your business. 
                Schedule a personalized demo today.
              </Typography>

              {/* CTA Button */}
              <Box sx={{ pt: 2 }}>
                <GradientButton
                  endIcon={<ChevronRight />}
                  size="large"
                  onClick={handleScheduleClick}
                >
                  Schedule a Demo
                </GradientButton>
              </Box>
            </Stack>
          </GlowBox>
        </Fade>
      </Container>
    </BackgroundBox>
  );
};

export default ScheduleDemo;

